import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import { Reveal } from "./Reveal";
import { KineticLines } from "./Kinetic";
import { PhotoTag } from "./PhotoTag";
import { BUSINESS } from "../data/site";

export const ServiceLayout = ({ eyebrow, titleLines, intro, image, imageAlt, items, note, preselect, children, secondImage, secondImageAlt, stockImage = true, stockSecond = true }) => (
    <main className="pt-16">
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-16 lg:grid-cols-12 lg:py-24">
            <div className="lg:col-span-6">
                <p className="eyebrow text-forest-leaf">{eyebrow}</p>
                <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl">
                    <KineticLines lines={titleLines} />
                </h1>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-moss">{intro}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to={`/book-now?services=${encodeURIComponent(preselect)}`} data-testid="service-book-button"
                        className="rounded-full bg-forest px-6 py-3 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                        Book This Service <ArrowRight size={14} className="ml-1 inline" />
                    </Link>
                    <a href={BUSINESS.phoneHref} data-testid="service-call-button"
                        className="flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 text-sm font-bold text-forest transition-colors hover:bg-bone-subtle">
                        <Phone size={14} /> {BUSINESS.phone}
                    </a>
                </div>
                <p className="mt-5 text-sm text-moss">
                    Prefer a price first?{" "}
                    <Link to={`/book-now?type=quote&services=${encodeURIComponent(preselect)}`} data-testid="service-quote-link"
                        className="font-bold text-forest underline underline-offset-4 transition-colors hover:text-forest-leaf">
                        Get a free quote
                    </Link>
                </p>
            </div>
            <Reveal className="lg:col-span-6" delay={0.15}>
                <div className="photo-frame relative overflow-hidden rounded-3xl border border-forest/15">
                    <img src={image} alt={imageAlt} className="aspect-[4/3] w-full object-cover" />
                    {stockImage && <PhotoTag />}
                </div>
            </Reveal>
        </section>

        <section className="bg-bone-subtle py-16 lg:py-24">
            <div className="mx-auto max-w-6xl px-6">
                <Reveal>
                    <p className="eyebrow text-forest-leaf">What we do</p>
                    <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">Services included</h2>
                </Reveal>
                <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((item, i) => {
                        const name = typeof item === "string" ? item : item.name;
                        const desc = typeof item === "string" ? null : item.desc;
                        return (
                            <Reveal key={name} delay={i * 0.06}>
                                <div className="flex h-full items-start gap-3 rounded-2xl border border-forest/10 bg-bone-card p-6">
                                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-forest text-bone">
                                        <Check size={13} />
                                    </span>
                                    <div>
                                        <p className="text-sm font-semibold text-ink">{name}</p>
                                        {desc && <p className="mt-1.5 text-sm leading-relaxed text-moss">{desc}</p>}
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
                {note && (
                    <Reveal delay={0.1}>
                        <div className="mt-10 rounded-2xl border border-ochre/40 bg-ochre/10 p-6 lg:p-8">
                            <p className="text-sm leading-relaxed text-ink">{note}</p>
                        </div>
                    </Reveal>
                )}
            </div>
        </section>

        {children}

        {secondImage && (
            <section className="mx-auto max-w-6xl px-6 py-16">
                <Reveal>
                    <div className="photo-frame relative overflow-hidden rounded-3xl border border-forest/15">
                        <img src={secondImage} alt={secondImageAlt} className="aspect-[16/10] w-full object-cover" />
                        {stockSecond && <PhotoTag />}
                    </div>
                </Reveal>
            </section>
        )}

        <section className="bg-forest py-16 text-center text-bone lg:py-20">
            <Reveal>
                <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Ready to book?</h2>
                <p className="mx-auto mt-3 max-w-xl text-sm text-bone/70">
                    Pick one service or combine it with others in a single booking — whatever your property needs.
                </p>
                <Link to={`/book-now?services=${encodeURIComponent(preselect)}`} data-testid="service-bottom-book-button"
                    className="mt-8 inline-block rounded-full bg-ochre px-8 py-3.5 text-sm font-bold text-forest-night transition-colors hover:bg-bone">
                    Book Now <ArrowRight size={14} className="ml-1 inline" />
                </Link>
            </Reveal>
        </section>
    </main>
);
